import React, { useEffect, useState } from 'react';
import { DashboardActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import T from 'components/common/text/T';
import { icon_refresh } from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import CustomTranslateTooltip from 'components/common/tooltip/CustomTranslateTooltip';
import PatientToothTooltip from 'components/dashboard/PatientToothTooltip';
import { useHistory } from 'react-router-dom';
import { useDidUpdateEffect } from 'lib/utils';
import ListEmpty from 'components/common/list/ListEmpty';
import CircularLoading from 'components/base/loading/CircularLoading';
import { PageSubTitle } from 'components/base/title/PageTitle';

interface PatientScacnProps {
  className?: string;
}

function PatientScacnContainer({ className }: PatientScacnProps) {
  const {
    language,
    scanPatients: { list: scanWaitingPatients },
    scanPatientsSuccess,
    scanPatientsPending,
    scanPatientsFailure,
  }: {
    language: string;
    scanPatients: { list: [] };
    scanPatientsSuccess: boolean;
    scanPatientsPending: boolean;
    scanPatientsFailure: boolean;
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    scanPatients: state.dashboard.scanPatients.data || { list: [] },
    scanPatientsSuccess: state.dashboard.scanPatients.success,
    scanPatientsPending: state.dashboard.scanPatients.pending,
    scanPatientsFailure: state.dashboard.scanPatients.failure,
  }));

  const { t } = useTranslation();
  const history = useHistory();
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);

  useEffect(() => {
    DashboardActions.fetch_scan_patients_request();
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  const handleRefreshList = () => {
    DashboardActions.fetch_scan_patients_request();
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  const handleClickProject = (projectCode: number) => {
    history.push(`/project/detail/${projectCode}`);
  };

  useDidUpdateEffect(() => {
    if (!scanPatientsSuccess) return;
    setIsLoadSuccess(true);
  }, [scanPatientsSuccess]);

  useDidUpdateEffect(() => {
    if (!scanPatientsFailure) return;
    setIsLoadFailure(true);
  }, [scanPatientsFailure]);

  return (
    <StyledPatientScan data-component-name="PatientScan" className={className} lang={language}>
      <div className="dashboard__card_title_box">
        <h2 className="dashboard__card_title">
          <PageSubTitle title="HOME_TITLE_PATIENT_SCAN" margin="0" />
        </h2>
        <MuiButton
          config={{
            bgColor: '#fff',
            borderColor: '#e1e1e1',
            width: '50px',
          }}
          className="xs dashboard__card_refresh"
          variant="outlined"
          onClick={handleRefreshList}
        >
          <img src={icon_refresh} />
        </MuiButton>
      </div>

      <div className="dashboard__patient_scan_wrapper box-shadow-default">
        <div className="dashboard__patient_scan_container">
          {scanWaitingPatients.length < 1 && scanPatientsPending && (
            <div className="infinite-scroll-loading">
              <CircularLoading />
            </div>
          )}
          {!!isLoadSuccess &&
            (scanWaitingPatients.length > 0 ? (
              scanWaitingPatients.map((item: any, idx: number) => {
                return (
                  <PatientScanListItem
                    key={idx}
                    className="dashboard__patient_scan_item_box"
                    onClickProject={handleClickProject}
                    scanPatient={item}
                  />
                );
              }, [])
            ) : (
              <ListEmpty text="GLOBAL_EMPTY_LIST_PATIENT" />
            ))}
          {!!isLoadFailure && <ListEmpty text="GLOBAL_LIST_ERROR" isRefreshButton={true} />}
        </div>
      </div>
    </StyledPatientScan>
  );
}

const StyledPatientScan = styled.section<{}>`
  .dashboard__card_title_box {
    display: flex;
    .dashboard__card_refresh {
      height: 34px;
    }
  }

  .dashboard__patient_scan_wrapper {
    width: 395px;
    height: 360px;
    background-color: white;
    border-radius: 10px;
    .dashboard__patient_scan_container {
      width: 100%;
      height: 360px;
      overflow: overlay;
      position: relative;
      .dashboard__patient_scan_item_box {
        border-bottom: 1px solid #e1e1e1;
        &:not(:last-child) {
        }
      }
    }
  }
`;

interface StyledPatientScanListItemProps {
  pItem?: any;
  className?: string;
  onClickProject: (projectCode: number) => void;
  scanPatient: any;
}

function PatientScanListItem({
  pItem,
  className,
  onClickProject,
  scanPatient,
}: StyledPatientScanListItemProps) {
  const { t } = useTranslation();
  const fullName = `${scanPatient?.firstName} ${
    !!scanPatient?.lastName ? scanPatient.lastName : ''
  }${!!scanPatient?.identification ? scanPatient.identification : ''}`;
  const teeth = scanPatient?.teeth;

  return (
    <StyledPatientScanListItem
      data-component-name="PatientScanListItem"
      className={className}
      onClick={() => {
        onClickProject(scanPatient?.projectCode);
      }}
    >
      <div className="patientProjectItem__info_box">
        <div className="patientProjectItem__info_row patient">
          <div className="patient__name_box">
            <div className="patient__name text-overflow-ellipsis">{fullName}</div>
          </div>
          <div className="patient__code">{scanPatient?.chartNumber}</div>
        </div>
        <div className="patientProjectItem__info_row project">
          <div className="project__name_box">
            <div className="project__name text-overflow-ellipsis">{scanPatient?.projectId}</div>
          </div>
        </div>
        <div className="patientProjectItem__info_row indication">
          <div className="project__indication_box">
            <CustomTranslateTooltip
              title={<PatientToothTooltip data={teeth} />}
              top="2px"
              width="310px"
              placement="bottom-end"
            >
              <div className="project__indication">
                {teeth?.map((item: any, idx: number) => {
                  if (idx > 13) return;
                  return (
                    <StyledPatientTeethListItem key={idx} color={item.color}>
                      {item?.toothNumber}
                    </StyledPatientTeethListItem>
                  );
                }, [])}
                {teeth?.length > 13 && (
                  <div className="custom_ellipsis_box">
                    <div className="custom_ellipsis">..</div>
                  </div>
                )}
              </div>
            </CustomTranslateTooltip>
          </div>
        </div>
      </div>
    </StyledPatientScanListItem>
  );
}

const StyledPatientScanListItem = styled.section<{}>`
  height: 105px;
  padding: 15px 20px;
  display: flex;
  column-gap: 10px;
  position: relative;
  &:hover {
    cursor: pointer;
    background-color: #f4f4f4;
  }
  .patientProjectItem__info_box {
    width: 355px;
    display: inline-flex;
    flex-direction: column;
    justify-content: space-between;

    .patientProjectItem__info_row {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      &.patient {
        height: 20px;
        .patient__name_box {
          width: 225px;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          .patient__name {
            width: 100%;
            height: 20px;
            line-height: 20px;
            font-size: 14px;
            color: #444444;
            font-weight: bold;
          }
        }
        .patient__code {
          width: 130px;
          height: 20px;
          line-height: 20px;
          font-size: 14px;
          color: #b6b6b6;
          display: flex;
          justify-content: flex-end;
        }
      }
      &.project {
        height: 20px;
        .project__name_box {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          .project__name {
            width: 100%;
            height: 20px;
            line-height: 20px;
            font-size: 13px;
            color: #444444;
          }
        }
      }
      &.indication {
        .project__indication_box {
          width: 355px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          .project__indication {
            width: 100%;
            height: 20px;
            display: inline-flex;
            overflow: hidden;
            column-gap: 4px;
            justify-content: flex-end;
            .project__indication_item {
              width: 20px;
              height: 20px;
              border: 1px solid #614ab8;
              color: #614ab8;
              border-radius: 50%;
              font-size: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .custom_ellipsis_box {
              height: 20px;
              display: flex;
              align-items: flex-end;
            }
          }
        }
      }
    }
  }
`;
const StyledPatientTeethListItem = styled.div<{ color: string }>`
  width: 20px;
  height: 20px;
  border: ${({ color }) => `1px solid ${color}`};
  color: ${({ color }) => `${color}`};
  border-radius: 50%;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default React.memo(PatientScacnContainer);
